import React from 'react';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';
import Tooltip from 'flow/components/ui-components/Tooltip';
import { twMerge } from 'tailwind-merge';

interface MeetingButtonProps {
    iconType: IconKey;
    callback: () => void;
    active?: boolean;
    hoverText?: string;
    disabled?: boolean;
    circular?: boolean;
    /**
     * Render the active state as a subtly lighter background with the icon
     * color unchanged, instead of the prominent inverted (white/red) style.
     */
    subtleActive?: boolean;
    /**
     * The right-end panel-toggle buttons (participants/host-controls/chat/
     * info) invert to a solid white circle with a dark icon on hover, instead
     * of just brightening the dark background — matches the Figma
     * "Suggestion" video. Independent of active/subtleActive, so it applies
     * whether or not that button's panel is currently open.
     */
    invertOnHover?: boolean;
    /**
     * Render as the waiting-room pill does — white, dark icon — for a button
     * that has stopped being ambient and now wants a decision (VID-76). Outranks
     * `active`: the panel being open does not settle the queue, so the button
     * keeps asking until the queue is empty.
     */
    highlighted?: boolean;
}

const MeetingButton: React.FC<MeetingButtonProps> = ({
    iconType,
    active,
    callback,
    hoverText,
    disabled,
    circular,
    subtleActive,
    invertOnHover,
    highlighted,
}) => {
    const getVariantClasses = () => {
        if (disabled) {
            return 'bg-white/5 cursor-default text-gray-600';
        }

        const hoverClass = invertOnHover
            ? 'hover:bg-white hover:text-gray-900'
            : 'hover:brighten-10';

        if (highlighted) {
            return 'bg-white text-gray-800 hover:bg-gray-200';
        }
        if (active) {
            return subtleActive
                ? twMerge('bg-gray-700', hoverClass)
                : 'bg-white text-red hover:bg-white/80';
        }
        return twMerge('bg-gray-800', hoverClass);
    };

    return (
        <Tooltip
            label={hoverText || ''}
            placement="top"
            tooltipClassName="text-white text-sm font-light py-2"
        >
            <button
                type="button"
                aria-label={hoverText}
                className={twMerge(
                    'focus-ring flex h-12 w-12 cursor-pointer items-center justify-center rounded-xl border-none',
                    circular ? 'rounded-full' : '',
                    getVariantClasses(),
                )}
                onClick={callback}
                data-test-id={`${iconType} - ${active ? 'active' : 'inactive'}`}
                disabled={disabled}
            >
                <Icon iconKey={iconType} className="!h-6 !w-6 p-0" />
            </button>
        </Tooltip>
    );
};

export default MeetingButton;
